// SA-locale numeric normalizer — CSV-import-boundary concern only (see
// services/api/src/modules/importer/rows.ts, the one caller). Real Unleashed
// exports produced on South-African-locale machines write numbers like
// "1 234,56" (space thousands separator — often U+00A0 NBSP or U+202F narrow
// NBSP rather than a plain space — and comma decimal separator), which the
// strict machine-format regexes in schemas/common.ts and schemas/importer.ts
// (quantitySchema, moisturePctSchema, opening-balance unitCostCents) mass-reject.
// This module is a PURE, deliberately CONSERVATIVE format normalizer: it turns
// a locale-formatted cell into plain machine format ("1234.56") so the strict
// schemas can validate it unchanged, or it REJECTS with an operator-actionable
// reason when the input is genuinely ambiguous or malformed. It never guesses —
// see the '1,234' example below.
//
// Deliberately NOT used anywhere else: the API's transactional endpoints keep
// their strict machine-format-only regexes. Locale tolerance is exclusively an
// import-time concern.
// Characters treated as thousands-group whitespace: regular space (U+0020),
// no-break space (U+00A0), and narrow no-break space (U+202F) - the three
// separators real-world SA-locale spreadsheet exports use interchangeably.
// Built from explicit \u escapes (not literal characters) so the source
// file never carries invisible/ambiguous bytes.
const THOUSANDS_SPACE_RE_SRC = '[\u0020\u00A0\u202F]';
const THOUSANDS_SPACE_RE = new RegExp(THOUSANDS_SPACE_RE_SRC);
const THOUSANDS_SPACE_RE_G = new RegExp(THOUSANDS_SPACE_RE_SRC, 'g');
function isAllowedChar(ch) {
    return (ch >= '0' && ch <= '9') || ch === '.' || ch === ',' || THOUSANDS_SPACE_RE.test(ch);
}
function countOccurrences(s, ch) {
    let count = 0;
    for (const c of s)
        if (c === ch)
            count++;
    return count;
}
/** First group may be 1-3 digits (the leading, possibly-short group); every
 * subsequent group must be EXACTLY 3 digits. Used for the "both '.' and
 * ',' present" rule (a value like "1.234.567,89" is valid; "12.34,5" — a
 * malformed leading/second group — is not). */
function isWellFormedGrouping(groups) {
    const [first, ...rest] = groups;
    if (first === undefined || !/^\d{1,3}$/.test(first))
        return false;
    return rest.every((g) => /^\d{3}$/.test(g));
}
function reject(reason) {
    return { ok: false, reason };
}
/** SPACE-separated thousands must be well-formed with the same rigor as
 * `isWellFormedGrouping` applies to dot/comma thousands (wave-gate fix
 * 2026-07-10: spaces used to be stripped UNCONDITIONALLY, so a malformed
 * '12 34' silently became '1234'). First segment 1-3 digits; every middle
 * segment exactly 3 digits; the LAST segment is exactly 3 digits optionally
 * followed by a decimal separator and the rest of the number (e.g. '234,56' —
 * the post-separator part is validated by the downstream dot/comma rules, not
 * here). Returns a rejection, or null when the grouping is fine (or there are
 * no spaces at all). Runs BEFORE the spaces are stripped. */
function checkSpaceGrouping(raw, body) {
    if (!THOUSANDS_SPACE_RE.test(body))
        return null;
    const segments = body.split(THOUSANDS_SPACE_RE_G);
    const first = segments[0];
    const last = segments[segments.length - 1] ?? '';
    const malformed = first === undefined ||
        !/^\d{1,3}$/.test(first) ||
        segments.slice(1, -1).some((g) => !/^\d{3}$/.test(g)) ||
        !/^\d{3}(?:[.,].*)?$/.test(last);
    if (malformed) {
        return reject(`'${raw}' space-separated thousands grouping is not well-formed (expected groups of 3 digits, e.g. '1 234 567,89') — write it unambiguously`);
    }
    return null;
}
function parseDotOnly(raw, sign, s, dotCount) {
    if (dotCount > 1) {
        return reject(`'${raw}' has more than one '.' — ambiguous, write it as a single plain number (e.g. '1234.56')`);
    }
    const idx = s.indexOf('.');
    const intPart = s.slice(0, idx);
    const fracPart = s.slice(idx + 1);
    if (!/^\d+$/.test(intPart) || !/^\d+$/.test(fracPart)) {
        return reject(`'${raw}' is not a valid number`);
    }
    return { ok: true, normalized: `${sign}${intPart}.${fracPart}` };
}
function parseCommaOnly(raw, sign, s, commaCount) {
    if (commaCount > 1) {
        return reject(`'${raw}' has more than one ',' — ambiguous, write it as a single plain number (e.g. '1234.56')`);
    }
    const idx = s.indexOf(',');
    const intPart = s.slice(0, idx);
    const fracPart = s.slice(idx + 1);
    if (!/^\d+$/.test(intPart) || !/^\d+$/.test(fracPart)) {
        return reject(`'${raw}' is not a valid number`);
    }
    // Comma followed by exactly 3 digits is the classic SA-vs-US ambiguity: it
    // could be a decimal ('1,234' meaning roughly 1.234) or a thousands group
    // ('1,234' meaning 1234). We never guess — reject and tell the operator
    // exactly how to disambiguate (goal spec's pinned example).
    if (fracPart.length === 3) {
        return reject(`ambiguous '${raw}' — write '${intPart}${fracPart}' or '${intPart}.${fracPart}'`);
    }
    if (fracPart.length === 1 || fracPart.length === 2) {
        return { ok: true, normalized: `${sign}${intPart}.${fracPart}` };
    }
    return reject(`'${raw}' — ',' followed by ${fracPart.length} digits isn't a recognized format; write it unambiguously as ` +
        `'${intPart}${fracPart}' (thousands) or '${intPart}.${fracPart}' (decimal)`);
}
function parseBothSeparators(raw, sign, s, dotCount, commaCount) {
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    // The separator occurring LAST (rightmost) in the string is the decimal
    // point; the other is the thousands separator (goal spec rule).
    const decimalChar = lastDot > lastComma ? '.' : ',';
    const thousandsChar = decimalChar === '.' ? ',' : '.';
    const decimalCount = decimalChar === '.' ? dotCount : commaCount;
    if (decimalCount > 1) {
        return reject(`'${raw}' has multiple decimal separators — ambiguous, write it as a single plain number`);
    }
    const decimalIdx = decimalChar === '.' ? lastDot : lastComma;
    const intPart = s.slice(0, decimalIdx);
    const fracPart = s.slice(decimalIdx + 1);
    if (fracPart.length === 0 || !/^\d+$/.test(fracPart)) {
        return reject(`'${raw}' is not a valid number`);
    }
    const groups = intPart.split(thousandsChar);
    if (!isWellFormedGrouping(groups)) {
        return reject(`'${raw}' thousands grouping is not well-formed (expected groups of 3 digits, e.g. '1.234.567,89') — write it unambiguously`);
    }
    const normalizedInt = groups.join('');
    return { ok: true, normalized: `${sign}${normalizedInt}.${fracPart}` };
}
/**
 * Normalizes a single CSV cell that is expected to hold a number, tolerating
 * South-African-locale formatting, into plain machine format ('1234.56') for
 * the strict zod schemas to validate. Returns `{ ok:false, reason }` — never
 * throws — when the cell cannot be interpreted safely.
 *
 * Rules (in order):
 * 1. Trim. Empty → reject.
 * 2. An optional leading '-' is the sign; a '-' anywhere else → reject.
 * 3. Only digits, '.', ',', and thousands-whitespace (space / U+00A0 / U+202F)
 *    are allowed characters — anything else (currency symbols, letters, ...) →
 *    reject.
 * 4. Regular space / NBSP / narrow NBSP between digit groups are thousands
 *    separators; the space-separated grouping must be WELL-FORMED (first
 *    group 1-3 digits, every later group exactly 3 — same rigor as rule 5's
 *    dot/comma grouping) or the cell is rejected.
 *    '1 234' → '1234'   '12 345 678' → '12345678'   '12 34' → reject
 * 5. Both '.' and ',' present → the RIGHTMOST is the decimal separator, the
 *    other is the thousands separator; the thousands grouping must be
 *    well-formed (groups of 3) or the cell is rejected.
 *    '1.234,56' → '1234.56'   '1,234.56' → '1234.56'
 * 6. Only ',' present, followed by 1-2 digits at the end → comma is the
 *    decimal separator.
 *    '12,5' → '12.5'   '0,5' → '0.5'
 * 7. Only ',' present, followed by EXACTLY 3 digits → AMBIGUOUS (could be
 *    1234 or 1.234) → reject with an actionable message.
 *    '1,234' → reject: "ambiguous '1,234' — write '1234' or '1.234'"
 * 8. Only '.' present (exactly one), or a plain integer → already machine
 *    format → pass through (after space-stripping).
 *    '1234.56' → '1234.56'   '1234' → '1234'
 * 9. Multiple '.' with no ',' (or multiple ',' with no '.') → ambiguous →
 *    reject.
 *
 * Examples: '1 234,56' → '1234.56'; '12,5' → '12.5'; '1.234,56' → '1234.56';
 * '1,234.56' → '1234.56'; '1,234' → reject (ambiguous); 'R100' → reject
 * (currency symbol); '-1 234,5' → '-1234.5'.
 */
export function parseLocaleNumber(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return reject(`'${raw}' is empty`);
    }
    let sign = '';
    let body = trimmed;
    if (body.startsWith('-')) {
        sign = '-';
        body = body.slice(1);
    }
    if (body.includes('-')) {
        return reject(`'${raw}' has a '-' that isn't a leading sign — expected a plain number`);
    }
    if (body.length === 0) {
        return reject(`'${raw}' has no digits`);
    }
    for (const ch of body) {
        if (!isAllowedChar(ch)) {
            return reject(`'${raw}' contains an unexpected character (${JSON.stringify(ch)}) — remove currency symbols/letters and write a plain number`);
        }
    }
    const spaceGroupingError = checkSpaceGrouping(raw, body);
    if (spaceGroupingError)
        return spaceGroupingError;
    const despaced = body.replace(THOUSANDS_SPACE_RE_G, '');
    if (despaced.length === 0) {
        return reject(`'${raw}' has no digits`);
    }
    const dotCount = countOccurrences(despaced, '.');
    const commaCount = countOccurrences(despaced, ',');
    if (dotCount === 0 && commaCount === 0) {
        if (!/^\d+$/.test(despaced)) {
            return reject(`'${raw}' is not a valid number`);
        }
        return { ok: true, normalized: `${sign}${despaced}` };
    }
    if (dotCount > 0 && commaCount > 0) {
        return parseBothSeparators(raw, sign, despaced, dotCount, commaCount);
    }
    if (commaCount > 0) {
        return parseCommaOnly(raw, sign, despaced, commaCount);
    }
    return parseDotOnly(raw, sign, despaced, dotCount);
}
//# sourceMappingURL=localeNumber.js.map